/**
 * The macOS desktop's own look — built ONLY from the shell tokens, so the
 * wallpaper, the window, the Dock and the menus flip with the theme like
 * every other layout. Translucent surfaces are `color-mix` over the theme
 * variables (never an rgba literal that is right in one theme only).
 *
 * The one deliberate exception is the traffic lights: their red / amber /
 * green is the control's identity and is the same in light and dark (as on
 * the real desktop); their glyphs sit on those fixed colours, so a fixed dark
 * glyph is readable in both themes.
 */
import type { CSSProperties } from 'react'
import { S } from '../../nav/tokens'

export const LIGHTS = {
  close: '#FF5F57',
  minimise: '#FEBC2E',
  zoom: '#28C840',
  glyph: 'rgba(0, 0, 0, 0.62)',
  ring: 'rgba(0, 0, 0, 0.18)',
}

/** A translucent version of a theme colour (glass surfaces). */
export function glass(color: string, pct: number): string {
  return `color-mix(in srgb, ${color} ${pct}%, transparent)`
}

/** The desktop picture: a quiet gradient of the app's accent over the page ground. */
export function wallpaper(accent: string): string {
  return [
    `radial-gradient(1100px 680px at 88% 112%, color-mix(in srgb, ${accent} 22%, transparent), transparent 62%)`,
    `radial-gradient(820px 560px at -6% 28%, color-mix(in srgb, ${accent} 13%, transparent), transparent 64%)`,
    `linear-gradient(168deg, color-mix(in srgb, ${accent} 9%, ${S.bg}) 0%, ${S.bg} 72%)`,
  ].join(', ')
}

/** Menu panels drawn by this layout (the Window menu) — the same surface the
 *  shared cascading menus use, so the two read as one menu bar. */
export const menuPanel: CSSProperties = {
  minWidth: 300, maxWidth: 380, maxHeight: '70vh', overflowY: 'auto', padding: 4,
  borderRadius: 10, background: S.raised, color: S.text,
  border: `1px solid ${S.border}`, boxShadow: '0 14px 34px rgba(0,0,0,0.24)',
}

/** Hover / focus behaviour inline styles cannot express — scoped to `tw-mac-*`. */
export const MAC_CSS = `
.tw-mac-light .tw-mac-glyph{opacity:0;transition:opacity .1s}
.tw-mac-lights:hover .tw-mac-glyph,.tw-mac-light:focus-visible .tw-mac-glyph{opacity:1}
.tw-mac-light:focus-visible{outline:2px solid var(--color-accent,#0061D0);outline-offset:1px;border-radius:50%}
.tw-mac-tile:hover{filter:brightness(1.07)}
@keyframes tw-mac-dock-in{from{opacity:0;scale:.96}to{opacity:1;scale:1}}
.tw-mac-tile:focus-visible{outline:2px solid var(--color-accent,#0061D0);outline-offset:3px}
.tw-mac-thumb .tw-mac-thumb-x{opacity:0;transition:opacity .1s}
.tw-mac-thumb:hover .tw-mac-thumb-x,.tw-mac-thumb:focus-within .tw-mac-thumb-x{opacity:1}
.tw-mac-thumb-x:focus-visible{outline:2px solid var(--color-accent,#0061D0);outline-offset:1px}
@media (hover:none){.tw-mac-thumb .tw-mac-thumb-x{opacity:1}.tw-mac-light .tw-mac-glyph{opacity:1}}
.tw-mac-row:disabled{opacity:.5;cursor:default}
.tw-mac-row:disabled:hover{background:transparent}
@media (prefers-reduced-motion:reduce){.tw-mac-tile:hover{filter:none}}
`
